import { useEffect, useState } from "react";
import {
  getDeviceCapabilities,
  shouldSimplifyAnimations,
  shouldDisableParallax,
  getOptimalAnimationDuration,
  getOptimalParallaxSpeed,
  type DeviceCapabilities,
} from "../utils/mobileOptimization";

export interface MobileOptimizationConfig {
  capabilities: DeviceCapabilities | null;
  shouldSimplifyAnimations: boolean;
  shouldDisableParallax: boolean;
  getOptimalDuration: (baseDuration: number) => number;
  getOptimalParallaxSpeed: (baseSpeed: number) => number;
  isLoading: boolean;
}

/**
 * Custom hook for mobile optimization
 * Detects device capabilities and provides optimization recommendations
 */
export const useMobileOptimization = (): MobileOptimizationConfig => {
  const [capabilities, setCapabilities] = useState<DeviceCapabilities | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const detectCapabilities = async () => {
      try {
        const caps = await getDeviceCapabilities();
        setCapabilities(caps);
      } catch (error) {
        console.error("Error detecting device capabilities:", error);
        // Set default capabilities on error
        setCapabilities({
          isMobile: false,
          isTablet: false,
          isTouch: false,
          supportsHover: true,
          supportsBackdropFilter: true,
          supportsWebP: false,
          hasReducedMotion: false,
          connectionSpeed: "medium",
          deviceMemory: 4,
          hardwareConcurrency: 4,
        });
      } finally {
        setIsLoading(false);
      }
    };

    detectCapabilities();
  }, []);

  return {
    capabilities,
    shouldSimplifyAnimations: capabilities
      ? shouldSimplifyAnimations(capabilities)
      : false,
    shouldDisableParallax: capabilities
      ? shouldDisableParallax(capabilities)
      : false,
    getOptimalDuration: (baseDuration: number) =>
      capabilities
        ? getOptimalAnimationDuration(baseDuration, capabilities)
        : baseDuration,
    getOptimalParallaxSpeed: (baseSpeed: number) =>
      capabilities
        ? getOptimalParallaxSpeed(baseSpeed, capabilities)
        : baseSpeed,
    isLoading,
  };
};

export default useMobileOptimization;
